import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiError = (detail) => {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

export default function AdminLogin() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("cv_admin_token", data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t.admin.loginFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen bg-ink grain-overlay relative flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <p className="font-serif text-3xl text-cream">
            Carlos <span className="italic text-gold">Vindel</span>
          </p>
          <p className="mt-2 text-cream/50 text-sm">{t.admin.loginSubtitle}</p>
        </div>
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="bg-cream p-8 sm:p-10 shadow-[0_24px_64px_rgba(0,0,0,0.35)]"
        >
          <h1 className="font-serif text-2xl text-ink flex items-center gap-3">
            <Lock className="w-5 h-5 text-gold" /> {t.admin.loginTitle}
          </h1>
          <div className="mt-7 space-y-5">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] font-semibold text-[#525D6B] mb-2">{t.admin.email}</label>
              <Input
                data-testid="admin-login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-white border-sand focus-visible:ring-gold rounded-none"
                required
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] font-semibold text-[#525D6B] mb-2">{t.admin.password}</label>
              <Input
                data-testid="admin-login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 bg-white border-sand focus-visible:ring-gold rounded-none"
                required
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-dark disabled:opacity-60 text-ink font-semibold text-sm tracking-wide px-8 py-4 transition-colors duration-300"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t.admin.signIn}
            </button>
          </div>
        </form>
        <div className="text-center mt-6">
          <Link to="/" data-testid="admin-back-to-site" className="inline-flex items-center gap-2 text-cream/50 hover:text-gold text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t.admin.backToSite}
          </Link>
        </div>
      </div>
    </main>
  );
}
